import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconArrowRight, IconCheck } from './Icons';
import { useLanguage } from '../context/LanguageContext';
import useScrollReveal from '../hooks/useScrollReveal';
import './Hero.css';

const Hero = () => {
  const { t } = useLanguage();
  const videoUrl = 'https://video.funnelcockpit.com/video/user/rwQQ7KQQKeKiy2TJy/video-player/mctq95jdmLMvhB6bY/480p.mp4';
  const showCta = true;
  const [isCtaExpanded, setIsCtaExpanded] = useState(false);
  const [isCtaBouncing, setIsCtaBouncing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoWrapperRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const expandTimer = setTimeout(() => {
      setIsCtaExpanded(true);
      setIsCtaBouncing(true);
      setTimeout(() => setIsCtaBouncing(false), 1000);
      setTimeout(() => setIsCtaExpanded(false), 10000);
    }, 30000);

    return () => clearTimeout(expandTimer);
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);
  
  const handleCtaClick = () => {
    if (!isCtaExpanded) {
      setIsCtaExpanded(true);
    }
  };

  const updateVideoScale = useCallback(() => {
    if (!videoWrapperRef.current) return;
    
    const rect = videoWrapperRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const scrollProgress = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight * 0.4)));
    const scale = 1 + (scrollProgress * 0.06);
    
    videoWrapperRef.current.style.setProperty('--video-scale', scale);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (Math.abs(currentScrollY - lastScrollY.current) < 5) return;
      lastScrollY.current = currentScrollY;
      
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      
      rafRef.current = requestAnimationFrame(updateVideoScale);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateVideoScale();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateVideoScale]);

  const heroReveal = useScrollReveal({ threshold: 0.05, rootMargin: '0px' });
  const videoReveal = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="hero-fox">
      <img 
        src="/img/background.png" 
        alt="" 
        className="hero-fox__background"
        width={1920}
        height={1080}
        loading="eager"
        fetchpriority="high"
        decoding="async"
      />
      <div className="hero-fox__container">
        <div ref={heroReveal.ref} className={`hero-fox__content reveal ${heroReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <span className="hero-fox__tagline reveal-child">{t.hero.tagline}</span>
          <h1 className="hero-fox__title reveal-child">
            {t.hero.title}{' '}
            <span className="hero-fox__title-gradient">{t.hero.titleHighlight}{t.hero.titleEnd || '.'}</span>
          </h1>
          {t.hero.bullets && (
            <ul className="hero-fox__bullets reveal-child">
              {t.hero.bullets.map((bullet, index) => (
                <li key={index} className="hero-fox__bullet">
                  <span className="hero-fox__bullet-icon">
                    <IconCheck size={14} />
                  </span>
                  <span className="hero-fox__bullet-text">{bullet}</span>
                </li>
              ))}
            </ul>
          )}
          {t.hero.bulletsCta && (
            <a
              href="https://meetings-eu1.hubspot.com/michael-wenger?uuid=9cf1d252-81d9-4d0a-ac62-21972be61c70"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-fox__bullets-cta"
            >
              {t.hero.bulletsCta}
            </a>
          )}
          <div className="hero-fox__trust-badges reveal-child">
            <div className="hero-fox__rating">
              <span className="hero-fox__rating-score">{t.hero.ratingScore}</span>
              <div className="hero-fox__stars">
                <span className="hero-fox__star">★</span>
                <span className="hero-fox__star">★</span>
                <span className="hero-fox__star">★</span>
                <span className="hero-fox__star">★</span>
                <span className="hero-fox__star">★</span>
              </div>
            </div>
            <div className="hero-fox__google-logo">
              <img 
                src="/img/google_logo.svg" 
                alt="Google" 
                className="hero-fox__google-image"
                width={92}
                height={30}
                loading="eager"
              />
            </div>
          </div>
        </div>
        <div className="hero-fox__video-caption">
          <span className="hero-fox__video-caption-label">{t.hero.videoCaption}</span>
          <div className="hero-fox__video-caption-info">
            <span className="hero-fox__video-caption-name">{t.hero.videoCaptionName}</span>
            <a href="https://www.natural-athlet.de" target="_blank" rel="noopener noreferrer" className="hero-fox__video-caption-pill">
              Natural Athlet
              <span className="hero-fox__video-caption-pill-arrow">↗</span>
            </a>
          </div>
        </div>
        <div 
          ref={(el) => { videoWrapperRef.current = el; videoReveal.ref.current = el; }}
          className={`hero-fox__video-wrapper reveal--scale ${videoReveal.isRevealed ? 'reveal--visible' : ''}`}
        >
          <video 
            ref={videoRef}
            className="hero-fox__video" 
            controls 
            playsInline
            preload="auto"
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <button 
            className={`hero-fox__play-btn ${isPlaying ? 'hero-fox__play-btn--hidden' : ''}`}
            onClick={togglePlay}
            aria-label={isPlaying ? t.hero.pause : t.hero.play}
          >
            <span className="hero-fox__play-icon">▶</span>
          </button>
        </div>
        <div className={`hero-fox__cta-border-wrap reveal--fade ${videoReveal.isRevealed ? 'reveal--visible reveal-delay-2' : ''}`}>
          <a href="https://meetings-eu1.hubspot.com/michael-wenger?uuid=9cf1d252-81d9-4d0a-ac62-21972be61c70" target="_blank" rel="noopener noreferrer" className="hero-fox__cta-button">
            <span className="hero-fox__cta-button-content">
              <span className="hero-fox__cta-button-title">{t.hero.ctaTitle}</span>
              <span className="hero-fox__cta-button-sub">{t.hero.ctaSub}</span>
            </span>
            <span className="hero-fox__cta-icon">
              <IconArrowRight size={14} />
            </span>
          </a>
        </div>
      </div>
      
      {showCta && (
        <div 
          className={`hero-fox__floating-cta-wrapper ${isCtaExpanded ? 'hero-fox__floating-cta-wrapper--expanded' : ''} ${isCtaBouncing ? 'hero-fox__floating-cta-wrapper--bouncing' : ''}`}
          onClick={handleCtaClick}
        >
          <div className={`hero-fox__floating-cta-mini ${isCtaExpanded ? 'hero-fox__floating-cta-mini--hidden' : ''}`}>
            <div className="hero-fox__floating-cta-mini-image">
              <img 
                src="/img/michael.png" 
                alt={t.hero.advisorAlt}
                width={55}
                height={55}
                loading="lazy"
              />
              <span className="hero-fox__floating-cta-mini-status"></span>
            </div>
            <span className="hero-fox__floating-cta-mini-pulse"></span>
          </div>
          
          <div className={`hero-fox__floating-cta-expanded ${isCtaExpanded ? 'hero-fox__floating-cta-expanded--visible' : ''}`}>
            <button 
              className="hero-fox__floating-cta-close"
              onClick={(e) => { e.stopPropagation(); setIsCtaExpanded(false); }}
              aria-label={t.hero.minimize}
            >
              ×
            </button>
            <a href="https://meetings-eu1.hubspot.com/michael-wenger?uuid=9cf1d252-81d9-4d0a-ac62-21972be61c70" target="_blank" rel="noopener noreferrer" className="hero-fox__floating-cta" onClick={(e) => e.stopPropagation()}>
              <span className="hero-fox__floating-cta-badge">{t.hero.free}</span>
              <div className="hero-fox__floating-cta-image-wrapper">
                <img 
                  src="/img/michael.png" 
                  alt={t.hero.advisorAlt} 
                  className="hero-fox__floating-cta-image"
                  width={60}
                  height={60}
                  loading="lazy"
                />
                <span className="hero-fox__floating-cta-status"></span>
              </div>
              <span className="hero-fox__floating-cta-text">{t.hero.ctaTitle}</span>
            </a>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
